import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'projects/uzurv/src/app/material.module';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Address } from '@uzurv/dots';
import { Driver, StateService } from '@uzurv/clara';
import { cloneDeep } from 'lodash';
import { isNullOrUndefined, PhoneNumberInputDirective } from '@uzurv/core';
import { DriverState } from 'projects/uzurv/src/store/driver/driver.reducer';
import { State, Store } from '@ngrx/store';
import { EditDriverProfile } from 'projects/uzurv/src/store/driver/driver.actions';
import { ZipCodeDirectiveModule } from '../../directives';

export interface EditDriverProfileDialogData {
  driver: Driver;
  driverAddress: Address;
  driverStateAddress: Address;
}

@Component({
  selector: 'app-edit-driver-profile-dialog',
  templateUrl: './edit-driver-profile-dialog.component.html',
  styleUrls: ['./edit-driver-profile-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MaterialModule, PhoneNumberInputDirective, ZipCodeDirectiveModule]
})
export class EditDriverProfileDialogComponent implements OnInit {
  editableAddress: Address;
  editableDriver: Driver;
  editDriverProfileForm: FormGroup;
  states = [];

  constructor(
    private dialogRef: MatDialogRef<EditDriverProfileDialogComponent>,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: EditDriverProfileDialogData,
    private store$: Store<State<DriverState>>,
    private stateService: StateService
  ) {}

  ngOnInit(): void {
    this.editableDriver = cloneDeep(this.data.driver);
    this.editableAddress = isNullOrUndefined(this.data.driverAddress) ? new Address() : cloneDeep(this.data.driverAddress);
    this.stateService.getStates().subscribe(states => {
      this.states = states;
    });
    this.initializeForm();

    this.editDriverProfileForm.get('hasNoMiddleName')?.valueChanges.subscribe((value: boolean) => {
      if (value) {
        this.editDriverProfileForm.get('middleName')?.disable();
        this.editableDriver.hasMiddleName = false;
      } else {
        this.editDriverProfileForm.get('middleName')?.enable();
        this.editableDriver.hasMiddleName = true;
      }
    });
  }

  initializeForm(): void {
    this.editDriverProfileForm = this.formBuilder.group({
      firstName: [this.editableDriver?.firstName, Validators.required],
      middleName: [{ value: this.editableDriver?.middleName, disabled: !this.editableDriver.hasMiddleName }],
      hasNoMiddleName: [!this.editableDriver.hasMiddleName],
      lastName: [this.editableDriver?.lastName, Validators.required],
      email: [{ value: this.editableDriver?.emailAddress, disabled: true }, Validators.required],
      phoneNumber: [{ value: this.editableDriver?.phoneNumber, disabled: true }, Validators.required],
      streetAddressOne: [this.editableAddress?.streetAddress1, Validators.required],
      streetAddressTwo: [this.editableAddress?.streetAddress2],
      city: [this.editableAddress?.city, Validators.required],
      state: [this.editableAddress?.stateCode, Validators.required],
      zipCode: [this.editableAddress?.zipCode, [Validators.pattern('^(?:\\d{5}(?:-\\d{4})?|\\d{9})$'), Validators.required]]
    });
  }

  handleCancelButtonClick() {
    this.dialogRef.close();
  }

  handleMiddleNameChange() {
    if (
      this.editDriverProfileForm.get('middleName')?.value?.trim().length === 0 ||
      this.editDriverProfileForm.get('hasNoMiddleName')?.value === true
    ) {
      this.editableDriver.middleName = null;
      this.editableDriver.hasMiddleName = false;
    } else {
      this.editableDriver.middleName = this.editDriverProfileForm.get('middleName').value;
    }
  }

  handleSave() {
    this.editableDriver.firstName = this.editDriverProfileForm.get('firstName').value;
    this.editableDriver.lastName = this.editDriverProfileForm.get('lastName').value;
    this.editableAddress.streetAddress1 = this.editDriverProfileForm.get('streetAddressOne').value;
    this.editDriverProfileForm.get('streetAddressTwo')?.value?.trim() === ''
      ? (this.editableAddress.streetAddress2 = null)
      : (this.editableAddress.streetAddress2 = this.editDriverProfileForm.get('streetAddressTwo').value);

    this.editableAddress.city = this.editDriverProfileForm.get('city').value;
    this.editableAddress.stateCode = this.editDriverProfileForm.get('state').value;
    this.editableAddress.zipCode = this.editDriverProfileForm.get('zipCode').value;
    this.editableAddress.driverId = this.editableDriver.dotsDriverIdentifier;
    this.editableAddress.id = null;
    this.editableAddress.verified = null;

    this.handleMiddleNameChange();
    this.dialogRef.close();
    this.store$.dispatch(EditDriverProfile({ driver: this.editableDriver, address: this.editableAddress }));
  }
}
